/**
 * PropertySelector Component
 * 
 * Component for selecting properties from an artifact with filtering by type.
 * Used in validation rules and property changes editors.
 */

import React, { useState, useMemo } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './PropertySelector.module.css';

const { Option, OptGroup } = Select;

interface PropertyDefinition {
  property_key?: string;
  name?: string;
  property_type?: string;
  type?: string;
  description?: string;
  input_type?: string;
  is_required?: boolean;
  required?: boolean;
  default_value?: any;
}

interface PropertySelectorProps {
  properties: PropertyDefinition[];
  value?: string;
  onChange?: (propertyKey: string | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  filterByType?: string[]; // Filter properties by type
  style?: React.CSSProperties;
}

export const PropertySelector: React.FC<PropertySelectorProps> = ({
  properties,
  value,
  onChange,
  placeholder = 'Select a property...',
  allowClear = true,
  disabled = false,
  filterByType,
  style
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and group properties
  const filteredProperties = useMemo(() => {
    let filtered = properties;

    // Filter by type if specified
    if (filterByType && filterByType.length > 0) {
      filtered = filtered.filter(prop => {
        const propType = prop.property_type || prop.type || '';
        return filterByType.includes(propType);
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(prop => {
        const name = prop.property_key || prop.name || '';
        const desc = prop.description || '';
        return name.toLowerCase().includes(term) || desc.toLowerCase().includes(term);
      });
    }

    // Group by type
    const grouped: Record<string, PropertyDefinition[]> = {};
    filtered.forEach(prop => {
      const propType = prop.property_type || prop.type || 'other';
      if (!grouped[propType]) {
        grouped[propType] = [];
      }
      grouped[propType].push(prop);
    });

    return grouped;
  }, [properties, filterByType, searchTerm]);

  const getPropertyKey = (prop: PropertyDefinition): string => {
    return prop.property_key || prop.name || '';
  };

  const getPropertyType = (prop: PropertyDefinition): string => {
    return prop.property_type || prop.type || 'unknown';
  };

  const getPropertyName = (prop: PropertyDefinition): string => {
    return prop.name || prop.property_key || '';
  };

  const getTypeColor = (type: string): string => {
    const typeColors: Record<string, string> = {
      string: 'blue',
      number: 'green',
      boolean: 'orange',
      uuid: 'purple',
      'uuid[]': 'purple',
      date: 'cyan',
      'date_range': 'cyan',
      array: 'geekblue',
      object: 'magenta',
      other: 'default'
    };
    return typeColors[type.toLowerCase()] || 'default';
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      showSearch
      filterOption={false}
      onSearch={setSearchTerm}
      style={style}
      optionLabelProp="label"
    >
      {Object.entries(filteredProperties).map(([type, props]) => (
        <OptGroup key={type} label={
          <span>
            <Tag color={getTypeColor(type)}>{type}</Tag>
            <span style={{ marginLeft: 8 }}>{props.length} {props.length === 1 ? 'property' : 'properties'}</span>
          </span>
        }>
          {props.map(prop => {
            const propKey = getPropertyKey(prop);
            const propName = getPropertyName(prop);
            const propType = getPropertyType(prop);
            const propDesc = prop.description || '';
            const isRequired = prop.is_required || prop.required || false;

            return (
              <Option
                key={propKey}
                value={propKey}
                label={propName}
              >
                <div className={styles.propertySelectorOption}>
                  <div className={styles.propertySelectorHeader}>
                    <span className={styles.propertySelectorName}>{propName}</span>
                    <Tag color={getTypeColor(propType)}>{propType}</Tag>
                    {isRequired && <Tag color="red">Required</Tag>}
                  </div>
                  {propDesc && (
                    <div className={styles.propertySelectorDescription}>
                      <Tooltip title={propDesc}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        <span>{propDesc.length > 60 ? `${propDesc.substring(0, 60)}...` : propDesc}</span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </Option>
            );
          })}
        </OptGroup>
      ))}
      {Object.keys(filteredProperties).length === 0 && (
        <Option disabled value="__no_results__">
          {searchTerm ? 'No properties found matching your search' : 'No properties available'}
        </Option>
      )}
    </Select>
  );
};
